/**
 * ▶️ **유튜브 안전 임베드** — 아동 안전(FR-COM-08 / S2-5, 불변식 6).
 *
 * ## 왜 shared 에 있나
 * 예전에는 이 판정이 웹에만 있었고, 관리자에는 **비슷하지만 다른 판정**이 따로 있었다
 * (`ArCardPanel.youtubeHint`). 그래서 운영자가 재생목록 주소(`?list=…`)나 짧은 아이디를
 * 넣으면 **관리자는 「괜찮아요」라고 하고 아이 화면은 「준비 중」**이 됐다 — 아무 에러 없이.
 * 이 저장소가 반나절을 태운 사고와 같은 모양이다(CLAUDE.md 「판정은 shared 한 곳에만」).
 *
 * 그래서 **띄울 수 있는가**를 정하는 곳은 여기 하나다. 관리자 안내도 이 함수로 판단한다.
 *
 * ## 무엇을 지키나
 * - `youtube-nocookie` — 추적 쿠키를 심지 않는다
 * - `rel=0` — 영상이 끝나고 **무관한 추천**이 뜨지 않는다(영유아 대상이라 이게 핵심이다)
 * - `modestbranding` · `playsinline` — 화면을 뺏기지 않는다
 * - 자동재생 없음(부르는 쪽이 클릭 뒤에만 붙인다)
 */

/** 주소에서 영상 아이디만. 못 알아보면 `null`. */
export function youtubeId(url: string | undefined | null): string | null {
  if (!url) return null;
  const m = /(?:youtu\.be\/|[?&]v=|embed\/)([\w-]{6,})/.exec(url);
  const id = m?.[1];
  // 자리표시자는 「아직 안 정함」이다 — 없는 영상으로 보내지 않는다
  return !id || id === "REPLACE" ? null : id;
}

/** 안전 임베드 주소. 못 띄울 주소면 `null`(부르는 쪽이 「준비 중」으로 그린다). */
export function youtubeEmbed(url: string | undefined | null): string | null {
  const id = youtubeId(url);
  return id ? `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1&playsinline=1` : null;
}

/**
 * 🧑‍💼 **운영자에게 할 말** — 이 주소로 영상이 뜨는가. 뜨면 `null`.
 *
 * 🔴 **막지는 않는다.** 저장은 되게 두고 무엇이 문제인지만 말한다 — 우리가 모르는 주소 형태가
 * 있을 수 있고, 운영자가 나중에 고칠 수도 있다. 다만 **「괜찮아요」라고 하지는 않는다**:
 * 화면에 안 뜰 주소를 통과시키면 운영자는 다 해 놓은 줄 알고 넘어간다.
 */
export function youtubeSay(url: string | undefined | null): string | null {
  const v = (url ?? "").trim();
  if (!v) return null; // 안 적은 것은 흠이 아니다
  if (!/^https:\/\//.test(v)) return "https:// 로 시작해야 해요.";
  if (v.includes("REPLACE")) return "아직 자리표시자예요 — 진짜 주소로 바꿔 주세요.";
  if (youtubeEmbed(v)) return null; // 띄울 수 있다
  if (/youtu\.be|youtube\.com/.test(v)) {
    // 유튜브인 건 맞는데 **영상 하나를 못 집는다**(재생목록·채널·짧은 아이디)
    return "이 주소로는 영상을 못 띄워요. 영상 하나의 주소(youtu.be/… 또는 watch?v=…)로 넣어 주세요.";
  }
  return "유튜브 주소가 아닌 것 같아요. 영상 하나의 주소로 넣어 주세요.";
}
